import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {HStack, Icon, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {
  CustomNoticeColorSchema,
  CustomNoticeIconSchema,
  CustomNoticeListItem,
  CustomNoticeRequestBody,
  CustomNoticeRequestBodySchema,
  useCreateCustomNoticeMutation,
  useUpdateCustomNoticeMutation,
} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {CUSTOM_NOTICE_ICONS} from '../../constants/customNoticeIcons';
import {useCanAddCustomNotice} from '../../hooks/useCanAddCustomNotice';
import {CustomNoticeColorDot} from '../CustomNoticeColorDot/CustomNoticeColorDot';

interface CustomNoticeDialogBaseProps extends TestIdProps {
  resourceId: EntityResourceIds;
  recordId: string;
}

interface CreateCustomNoticeDialogProps extends CustomNoticeDialogBaseProps {
  mode: 'create';
}

interface EditCustomNoticeDialogProps extends CustomNoticeDialogBaseProps {
  mode: 'edit';
  notice: CustomNoticeListItem;
}

type CustomNoticeDialogProps = CreateCustomNoticeDialogProps | EditCustomNoticeDialogProps;

export function CustomNoticeDialog(props: CustomNoticeDialogProps) {
  const isEdit = props.mode === 'edit';

  const [createCustomNotice] = useCreateCustomNoticeMutation();
  const [updateCustomNotice] = useUpdateCustomNoticeMutation();

  const {canAddCustomNotice} = useCanAddCustomNotice({
    resourceId: props.resourceId,
    recordId: props.recordId,
  });

  // The dialog is part of the library's public API, so it guards the write itself instead of
  // trusting whoever opened it to have checked the permission first
  const isSubmitForbidden = match(props)
    .with({mode: 'edit'}, (editProps) => editProps.notice.canUpdate !== true)
    .otherwise(() => !canAddCustomNotice);

  const defaultValues: CustomNoticeRequestBody = {
    text: isEdit ? props.notice.text : '',
    color: isEdit ? props.notice.color : 'neutral',
    icon: isEdit ? props.notice.icon : 'information',
  };

  const handleSubmit: FormSubmitHandler<CustomNoticeRequestBody> = (values) => {
    const request = isEdit
      ? updateCustomNotice({
          resourceId: props.resourceId,
          recordId: props.recordId,
          customNoticeId: props.notice.id,
          body: values,
        })
      : createCustomNotice({
          resourceId: props.resourceId,
          recordId: props.recordId,
          body: values,
        });

    return request
      .unwrap()
      .then(() => {
        showNotification.success(
          i18n.t(
            isEdit
              ? 'general.notifications.changesSuccessfullySaved'
              : 'general.notifications.successfullyCreated'
          )
        );
        closeCurrentDialog();
      })
      .catch(handleApiError);
  };

  return (
    <Form
      defaultValues={defaultValues}
      experimentalZodSchema={CustomNoticeRequestBodySchema}
      onSubmit={handleSubmit}
    >
      {(control) => (
        <VStack spacing={4}>
          <FormField
            type="textarea"
            minRows={3}
            control={control}
            name="text"
            label={i18n.t('general.customNotices.labels.text')}
            isRequired
            data-testid={suffixTestId('customNoticeDialog-text', props)}
          />
          <FormField
            type="choice"
            control={control}
            name="color"
            label={i18n.t('general.customNotices.labels.color')}
            options={getCustomNoticeColorOptions()}
            isNotClearable
            menuInPortal
            formatOptionLabel={(option) => (
              <HStack spacing={3} align="center">
                <CustomNoticeColorDot color={getColorFromOptionValue(option?.value)} />
                <Text size="small">{option.label}</Text>
              </HStack>
            )}
            data-testid={suffixTestId('customNoticeDialog-color', props)}
          />
          <FormField
            type="choice"
            control={control}
            name="icon"
            label={i18n.t('general.customNotices.labels.icon')}
            options={getCustomNoticeIconOptions()}
            isNotClearable
            menuInPortal
            formatOptionLabel={(option) => (
              <HStack spacing={3} align="center">
                <Icon value={getIconFromOptionValue(option?.value)} size={4} />
                <Text size="small">{option.label}</Text>
              </HStack>
            )}
            data-testid={suffixTestId('customNoticeDialog-icon', props)}
          />
          <ButtonGroup align="right">
            <Button
              title={i18n.t('general.actions.discard')}
              variant="secondary"
              onClick={closeCurrentDialog}
              data-testid={suffixTestId('customNoticeDialog-discard', props)}
            />
            <FormButton
              control={control}
              type="submit"
              variant="primary"
              title={i18n.t(isEdit ? 'general.actions.save' : 'general.actions.create')}
              isDisabled={isSubmitForbidden}
              data-testid={suffixTestId('customNoticeDialog-submit', props)}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}

function getCustomNoticeIconOptions() {
  return CustomNoticeIconSchema.options.map((icon) => ({
    value: icon,
    label: i18n.t(`general.customNotices.icons.${icon}`),
  }));
}

function getCustomNoticeColorOptions() {
  return CustomNoticeColorSchema.options.map((color) => ({
    value: color,
    label: i18n.t(`general.labels.color.${color}`),
  }));
}

const getColorFromOptionValue = (value: string | number | Nullish) => {
  const parsedColor = CustomNoticeColorSchema.safeParse(value);
  return parsedColor.success ? parsedColor.data : 'neutral';
};

const getIconFromOptionValue = (value: string | number | Nullish) => {
  const parsedIcon = CustomNoticeIconSchema.safeParse(value);
  return parsedIcon.success ? CUSTOM_NOTICE_ICONS[parsedIcon.data] : undefined;
};
